import { ClientActionCacheService } from "@cervval.js/client-action/src/services/client.action.cache.service";
import { createHtmlElement, empty } from "@cervval.js/client-html5-core/src/tools/dom.tools";
import { ClientRouteOptions, TypedClientAjaxProxy } from "@cervval.js/client-html5-spa/routes";
import { CjsButton } from "@cervval.js/client-html5-ui/src/components/button/button";
import { CjsDialog, DialogPreConfig, DialogSize, showErrorInDialog } from "@cervval.js/client-html5-ui/src/components/dialog/dialog";
import { ACTION_CACHE_SERVICE } from "@cervval.js/common-action/src/services/cache.service";
import { AuthInfos, AuthInfosRoutes, LoginFormAuthRoutes, LoginParameters } from "@cervval.js/common-auth/src/authRoutes";
import { Injected } from "@cervval.js/common/src/dependencies/injected";
import { AbstractClientBookingService } from "@ehealth_tools/client-booking/src/services/abstractClientBooking.service";
import { LoginComponent } from "@ehealth_tools/client/src/components/loginComponent/loginComponent";
import { BookingCommonUser, BookingProduct, BookingSession } from "@ehealth_tools/common-booking/src/data/booking.dto";
import { AuthBookingRoutes } from "@ehealth_tools/common-booking/src/routes/authBooking.routes";
import { LatLng } from "@ehealth_tools/common-map/src/map.definitions";
import { BuildPasswordStatus, SimpleLoginRoutes } from "@ehealth_tools/simple-login-common/src/simpleLogin.routes";
import { ElementTypes } from "@sportsante/common-shared/src/data/global/global.model";
import { AppLoginData } from "@sportsante/common-shared/src/data/users/appLoginData";
import { CompanyAffectationDTO, CompanyDTO } from "@sportsante/common-shared/src/data/users/company.dto";
import { UserDTO } from "@sportsante/common-shared/src/data/users/user.dto";
import { I18nNavHeader } from "../../core/components/navHeader/navHeader.i18n";
import { PatientRegistrationForm } from "../../core/components/patientRegistrationForm/patientRegistrationForm";

export class ClientBookingService extends AbstractClientBookingService<ElementTypes, UserDTO, CompanyAffectationDTO, AppLoginData> {

    @Injected(ACTION_CACHE_SERVICE)
    private _actionCacheService!: ClientActionCacheService<ElementTypes, unknown>;

    @TypedClientAjaxProxy()
    public override readonly authBookingRoutes!: AuthBookingRoutes<ClientRouteOptions>;

    @TypedClientAjaxProxy()
    private readonly authInfosRoutes!: AuthInfosRoutes<ClientRouteOptions>;

    @TypedClientAjaxProxy()
    public readonly simpleLoginRoutes!: SimpleLoginRoutes<ClientRouteOptions>;

    @TypedClientAjaxProxy()
    private readonly loginRoutes!: LoginFormAuthRoutes<AppLoginData, ClientRouteOptions>;

    /** */
    public override get googleMapToken(): string {
        return "AIzaSyCxWI7xSqlPrLnaYyHjz8xTK-5Gk3n09eQ";
    }
    /** */
    public override get googleMapUid(): string {
        return "5477dec7474e2633";
    }

    /** */
    public override getSessionLabel(session: BookingSession): string {
        const product = this.getById<BookingProduct>("bookingProducts", session.productId);
        return `Session ${product.name}`;
    }

    /** @override */
    public override getSessionTooltip(session: BookingSession): string | undefined {
        const product = this.getById<BookingProduct>("bookingProducts", session.productId);
        return this.getSessionLabel(session) + " - " + (session.description ?? product.description);
    }

    /** Build static url for the image */
    public override getSnippetURL(user: BookingCommonUser, prefix?: string): string | null {
        if (user.imageId == null) { return null; }
        const attachment = this._actionCacheService.getDTOCacheRepository("attachments").getDTOById(user.imageId);
        return this._deduplicateSlashInPath(`${prefix ?? ""}${"/image"}/${attachment.fileName}`);
    }

    /** */
    public _deduplicateSlashInPath(url: string): string {
        const [protocol, rest] = url.split("://");
        if (!rest) { return url; }
        return `${protocol}://${rest.replace(/\/{2,}/, "/")}`;
    }

    /** Called to save location extracted from google map */
    public override saveLocation(dto: CompanyDTO, localization: LatLng): Promise<void> {
        this.simpleLoginRoutes.amIConnected().then(async res => {
            if (res) {
                await this.actionService.encapsulateInAction(action => {
                    action.updateValuesIfNeeded("companies", dto, { lat: localization.lat, lng: localization.lng });
                }, {
                    logMessage: "Save location"
                });
            }
        }).catch(showErrorInDialog);

        return Promise.resolve();
    }

    /** Show login dialog */
    public override showLogin(): void {
        this.authInfosRoutes.getAuthInfos().then((authInfos: AuthInfos) => {
            const dialog = new CjsDialog({
                size: DialogSize.DEFAULT,
                closeOnEscapeKey: true,
                preConfig: DialogPreConfig.NO_BUTTON
            });
            dialog.classList.add("login-dialog");
            dialog.addToBody(this._buildLoginComponent(authInfos));

            // registration
            const div = createHtmlElement("div", { cssClass: ["flex-container-column", "align-items-center", "justify-content-center", "gap-2"] });
            const noAccount = createHtmlElement("a", { innerHTML: I18nNavHeader().login.noAccount, cssClass: ["anchor-element"] });

            noAccount.addEventListener("click", () => {
                dialog.options.setAndRefreshIfChanged({
                    size: DialogSize.LARGE,
                    title: "Formulaire Inscription"
                });
                empty(dialog.components.dialogBody);
                const proRedirection = new CjsButton({
                    cssClasses: ["btn-coach-redirection"],
                    outline: true,
                    label: I18nNavHeader().login.proRedirection
                });
                const patientRedirection = new CjsButton({
                    cssClasses: ["btn-patient-redirection"],
                    outline: true,
                    label: I18nNavHeader().login.patientRedirection
                });
                proRedirection.clickEvent.on(_ => {
                    window.location.href = "/login/#registration";
                });

                patientRedirection.clickEvent.on(_ => {
                    dialog.options.setAndRefreshIfChanged({
                        size: DialogSize.LARGE,
                        title: "Formulaire Inscription"
                    });
                    empty(dialog.components.dialogBody);

                    const newForm = new PatientRegistrationForm();
                    dialog.addToBody(newForm);
                    newForm.saveEvent.on(() => {
                        empty(dialog.components.dialogBody);
                        dialog.addToBody(I18nNavHeader().successRegistrationHTML);
                    });
                });
                const proRedirectionDiv = createHtmlElement("div", { cssClass: ["flex-container-row", "align-items-center", "justify-content-center", "mb-2", "gap-3"] });
                proRedirectionDiv.append(proRedirection);
                proRedirectionDiv.append(patientRedirection);
                dialog.addToBody(proRedirectionDiv);

            });

            div.append(noAccount);
            dialog.addToBody(div);
        }).catch(showErrorInDialog);
    }

    /**
     * build LoginComponent from authInfos
     */
    protected _buildLoginComponent(authInfos: AuthInfos): LoginComponent {
        const loginComponent: LoginComponent = new LoginComponent({
            login: (loginS, password) => this._onFormLogin({ login: loginS, password }, authInfos, loginComponent),
            sendCallback: (uid) => {
                return this.simpleLoginRoutes.buildPassword({ login: uid }).then((result) => {
                    if (result.status === BuildPasswordStatus.SUCCESS && result.login != null) {
                        return true;
                    }
                    else {
                        loginComponent.showError(I18nNavHeader().login.error);
                        return false;
                    }
                });
            },
            nbrDigitPassword: 6
        });

        loginComponent.components.loginInput.setAttribute("autocapitalize", "off");
        loginComponent.components.loginInput.setAttribute("autocorrect", "off");
        loginComponent.components.loginInput.removeAttribute("autofocus");

        return loginComponent;
    }

    /**
     * This function is called when user triggers a form login
     */
    protected _onFormLogin(loginParameters: LoginParameters, authInfos: AuthInfos, loginComponent: LoginComponent): Promise<void> {
        return this.loginRoutes.formLogin(loginParameters, { doNotLogErrors: true, doNotShowDialogOnErrors: true }).then((loginResult) => {
            location.reload();
        }).catch(() => {
            loginComponent.showError(I18nNavHeader().login.loginError);
        });
    }
}